import { useEffect, useState } from 'react';
import StepsAnnual from './components/StepsAnnual';
import {
	CaretCircleLeft,
	CaretLeft,
	CaretRight,
	CheckCircle,
	DownloadSimple,
	Files,
} from '@phosphor-icons/react';
import PrimaryButton from '../../components/atoms/Button';
import Loading from '../../components/atoms/Loading';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { PRODUCTS_SLUG } from '../../shared/constants/SlugsProducts';
import { DATES } from '../../shared/utils/dates';
import { setShowModalCheckout } from '../../redux/slices/plans.slice';
import useAddToCart from '../../hooks/useAddToCart';
import AnnualPreviewStep1 from './previewSteps/AnnualPreviewStep1';
import AnnualPreviewStep2 from './previewSteps/AnnualPreviewStep2';
import AnnualPreviewStepFinal from './previewSteps/AnnualPreviewStepFinal';
import { SATLinkStatus } from '../../@types/SATLinking';
import UnlinkedUser from '../home/organisms/UnlinkedUser';
import Modal from '../../components/molecules/Modal/Modal';
import AnnualCountdown from '../plansAndPricesV2/molecules/AnnualCountdown';
import ROUTES from '../../shared/constants/routes';
import { useNavigate } from 'react-router-dom';
import usePurchases from '../../hooks/usePurchases';
import { DeclarationOverviewStatus } from '../../components/organisms/DeclarationsOverview/DeclarationsOverview';
import { getSupportIssues } from '../../services/user-interaction.service';
import { SupportIssue } from '../../@types/Profile.type';
import ModalPaymentInstallments from './components/ModalPaymentInstallments';
import { useService } from '../../hooks/useService';
import BannerAnnualPreview from '../../assets/banner_annual_preview.png';
import BannerAnnualPreviewMobile from '../../assets/banner_annual_preview_mobile.png';
import ModalAnnualPreferences from './components/ModalAnnualPreferences';
import { ModalPositiveBalance } from './components/ModalPositiveBalance';
import { AnnualFAQs } from './components/AnnualFAQs';
import Text from '../../components/atoms/Text';
import { Partiality } from '../../@types/FFT.type';
import { EVENT_TYPES } from '../../shared/constants/events';
import useIsMobile from '../../hooks/useIsMobile';
import usePageView from '../../hooks/usePageView';

const AnnualDeclarationPage = () => {
	const navigate = useNavigate();
	const userProfile = useSelector((state: RootState) => state.profile.user);
	const { status, isLoading: isLoadingSat } = useSelector(
		(state: RootState) => state.satLinkStatus,
	);
	const { declarationsOverviewDetail, currentAnnualDeclaration, allFFT } =
		useSelector((state: RootState) => state.tax);
	const completedAnnualDeclaration = allFFT?.platec?.annual?.find(
		(declaration) => declaration.status === 'completed',
	);
	const [isModalDownload, setIsModalDownload] = useState(false);

	const userHasPendingDeclarations = declarationsOverviewDetail?.some(
		(declaration) => {
			const declarationYear = declaration.declaration_period?.split('-')?.[1];
			return (
				declarationYear === '2024' &&
				declaration.obligation_compliance_check ===
					DeclarationOverviewStatus.MISSING &&
				declaration?.period_hired_with_heru === 'Period no contratado'
			);
		},
	);

	const { userHasAnnualSubscription, havePrebillingAvailable } = usePurchases();

	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [supportIssues, setSupportIssues] = useState<SupportIssue[]>([]);
	const { annualDeclarationOverview } = useSelector(
		(state: RootState) => state.tax,
	);
	const { fetchAnnualDeclarationOverview, getFFTs, getTaxPayer } = useService();
	const [showModal, setShowModal] = useState({
		positiveBalance: false,
		unlinked: false,
		paymentInstallments: false,
		annualPreferences: false,
	});
	const [step, setStep] = useState<number>(1);
	const isMobile = useIsMobile();

	const { handleAddCustomProduct, isLoading: isLoadingAddToCart } =
		useAddToCart();

	const { tokenSession } = useSelector((state: RootState) => state.auth);

	const userIsLinked = Boolean(status?.status === SATLinkStatus.ACTIVE);

	const { products } = useSelector((state: RootState) => state.productList);

	const regularizationProducts = products?.find(
		(product) => product.name === 'Declaración Anual',
	)?.products;

	const subscriptionProduct = regularizationProducts?.find(
		(product) => product.slug === PRODUCTS_SLUG.ANNUAL_DECLARATION,
	);

	const showBackButton = localStorage.getItem('annualPreviewHome');

	const { trackPageView } = usePageView({
		sectionVisit: EVENT_TYPES.PAGE_VIEW_ANNUAL_DECLARATION,
	});

	const fetchSupportIssues = async () => {
		try {
			const response = await getSupportIssues(tokenSession);
			setSupportIssues(response);
		} catch (error) {}
	};

	const handleAddToCart = async () => {
		const payload = [
			{
				product_version_id: subscriptionProduct?.default_product_version_id,
				additional_information: {
					regimes: [],
					year: DATES.currentYear - 1,
				},
			},
		];

		const response = await handleAddCustomProduct({
			payload: payload,
			navigateToCart: false,
			resetOrders: true,
		});

		if (!response) return;
		dispatch(setShowModalCheckout(true));
	};

	useEffect(() => {
		(async () => {
			if (!userProfile?.id) return;
			setIsLoading(true);
			await getTaxPayer(userProfile?.id);
			await fetchAnnualDeclarationOverview();
			await fetchSupportIssues();
			await getFFTs(userProfile?.id, String(DATES.currentYear));
			setIsLoading(false);
		})();
	}, [userProfile]);

	useEffect(() => {
		trackPageView();
	}, []);

	const MAX_STEPS = 3;

	const handleBackNavigation = () => {
		try {
			if (showBackButton === 'taxboard') {
				navigate(ROUTES.TAXES);
			} else {
				window.localStorage.removeItem('annualPreviewHome');
				setTimeout(() => {
					navigate(-1);
				}, 100);
			}
		} catch (error) {
			navigate(-1);
		}
	};

	const handleDownload = (url: string) => {
		window.open(url, '_blank');
	};

	if (currentAnnualDeclaration && currentAnnualDeclaration.detail_pdf) {
		return (
			<section className='py-4 px-2 md:py-6 md:px-12 flex flex-col gap-3 text-sm md:text-base max-w-[700px] mx-auto fade-in-scale '>
				<div className='flex flex-col bg-white rounded-xl gap-1 p-4 items-center relative shadow-sm text-center'>
					<CheckCircle
						size={44}
						weight='duotone'
						className='text-h-success-50'
					/>
					<h2 className=' text-2xl font-bold leading-tight'>
						Declaración <br className='hidden md:block' /> anual{' '}
						{currentAnnualDeclaration?.declaration_year} presentada
					</h2>
				</div>

				<iframe
					src={currentAnnualDeclaration.detail_pdf!}
					className='w-full h-[calc(100vh-400px)] border-0 rounded-lg'
					title='Recibo de compra'
				/>

				{((completedAnnualDeclaration?.additional_information &&
					completedAnnualDeclaration?.additional_information?.normal) ||
					(completedAnnualDeclaration?.additional_information &&
						completedAnnualDeclaration?.additional_information?.detail)) && (
					<div className='flex flex-col gap-2 w-full mt-2'>
						<PrimaryButton
							variant='secondary'
							label='Descargar declaración'
							onClick={() => setIsModalDownload(true)}
							sx='w-full px-4'
							beforeIcon={
								<Files
									size={16}
									color='#1D8FF3'
								/>
							}
						/>
					</div>
				)}

				<div className='bg-white rounded-xl p-2 mt-2 relative shadow-sm flex flex-col gap-2 items-start'>
					<p className='font-medium'>Preguntas frecuentes</p>
					<AnnualFAQs
						faqs={[
							{
								question:
									'Si tengo más de un régimen fiscal, ¿debí presentar varias declaraciones anuales',
								answer: `No, el SAT exige una única declaración anual que integre todos tus regímenes fiscales, incluyendo ingresos y deducciones de todas tus fuentes.`,
							},
							{
								question:
									'¿Qué ingresos y deducciones incluyen en la declaración anual?',
								answer: (
									<p>
										Se incluyen todos los ingresos y deducciones que obtuviste
										en el año fiscal inmediatamente anterior, es decir, el 2024.{' '}
										<a
											className='text-h-primary-50'
											href='https://noticias.heru.app/en/articles/5360705'
											target='_blank'
											rel='noopener noreferrer'
										>
											Click aquí para ver el artículo
										</a>{' '}
									</p>
								),
							},
							{
								question:
									'¿De qué depende el resultado de mi declaración anual?',
								answer: `El resultado de tu declaración anual depende directamente de la relación entre tus ingresos y deducciones durante el año fiscal 2024. En heru, nos encargamos de declarar correctamente todos tus ingresos y aplicar las deducciones a las que tienes derecho, asegurando que el resultado, ya sea un saldo a pagar o a favor, sea el adecuado y conforme a la normativa fiscal.`,
							},
						]}
					/>
				</div>
				<Modal
					open={isModalDownload}
					onClose={() => setIsModalDownload(false)}
					type='fullscreen'
					sx='w-[90%] md:w-[500px]'
				>
					<div className='flex flex-col gap-4'>
						<Text
							size='subtitle'
							weight='bold'
							color='medium'
							sx='mb-2'
						>
							Descargar declaración
						</Text>

						<Text
							size='body-3'
							weight='regular'
							color='medium'
							sx='mb-4'
						>
							Selecciona los documentos que deseas descargar:
						</Text>

						<div className='flex flex-col gap-3'>
							{completedAnnualDeclaration?.additional_information &&
							completedAnnualDeclaration?.additional_information.partialities
								?.length === 0 &&
							((completedAnnualDeclaration?.additional_information &&
								completedAnnualDeclaration?.additional_information?.normal) ||
								(completedAnnualDeclaration?.additional_information &&
									completedAnnualDeclaration?.additional_information
										?.detail)) ? (
								<>
									{completedAnnualDeclaration?.additional_information &&
										completedAnnualDeclaration?.additional_information
											?.normal && (
											<button
												onClick={() => {
													handleDownload(
														completedAnnualDeclaration?.additional_information
															?.normal || '',
													);
													setIsModalDownload(false);
												}}
												className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
											>
												<div className='flex items-center gap-3'>
													<Files
														size={20}
														weight='fill'
														className='text-h-primary'
													/>
													<Text
														size='body-3'
														weight='medium'
													>
														Declaración anual
													</Text>
												</div>
												<DownloadSimple
													size={18}
													weight='bold'
													className='text-h-primary'
												/>
											</button>
										)}

									{completedAnnualDeclaration?.additional_information &&
										completedAnnualDeclaration?.additional_information
											?.detail && (
											<button
												onClick={() => {
													handleDownload(
														completedAnnualDeclaration?.additional_information
															?.detail || '',
													);
													setIsModalDownload(false);
												}}
												className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
											>
												<div className='flex items-center gap-3'>
													<Files
														size={20}
														weight='fill'
														className='text-h-primary'
													/>
													<Text
														size='body-3'
														weight='medium'
													>
														Detalle de declaración
													</Text>
												</div>
												<DownloadSimple
													size={18}
													weight='bold'
													className='text-h-primary'
												/>
											</button>
										)}
								</>
							) : (
								<>
									{completedAnnualDeclaration?.additional_information &&
										completedAnnualDeclaration?.additional_information
											?.normal && (
											<button
												onClick={() => {
													handleDownload(
														completedAnnualDeclaration?.additional_information
															?.normal || '',
													);
													setIsModalDownload(false);
												}}
												className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
											>
												<div className='flex items-center gap-3'>
													<Files
														size={20}
														weight='fill'
														className='text-h-primary'
													/>
													<Text
														size='body-3'
														weight='medium'
													>
														Declaración anual
													</Text>
												</div>
												<DownloadSimple
													size={18}
													weight='bold'
													className='text-h-primary'
												/>
											</button>
										)}

									{completedAnnualDeclaration?.additional_information &&
										completedAnnualDeclaration?.additional_information
											?.detail && (
											<button
												onClick={() => {
													handleDownload(
														completedAnnualDeclaration?.additional_information
															?.detail || '',
													);
													setIsModalDownload(false);
												}}
												className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
											>
												<div className='flex items-center gap-3'>
													<Files
														size={20}
														weight='fill'
														className='text-h-primary'
													/>
													<Text
														size='body-3'
														weight='medium'
													>
														Detalle de declaración
													</Text>
												</div>
												<DownloadSimple
													size={18}
													weight='bold'
													className='text-h-primary'
												/>
											</button>
										)}

									{/* Sección para pagos en parcialidades */}
									{completedAnnualDeclaration?.additional_information &&
										completedAnnualDeclaration?.additional_information
											.partialities?.length > 0 && (
											<div className='mt-2'>
												<Text
													size='body-3'
													weight='medium'
													sx='mb-4'
												>
													Pagos en parcialidades
												</Text>
												<div className='flex flex-col gap-2'>
													{completedAnnualDeclaration?.additional_information.partialities.map(
														(installment: Partiality) => (
															<button
																key={installment.partiality_number}
																onClick={() => {
																	handleDownload(installment.url);
																	setIsModalDownload(false);
																}}
																className='flex items-center justify-between p-3 bg-h-primary-95 rounded-lg hover:bg-h-primary-90 transition-colors'
															>
																<div className='flex items-center gap-3'>
																	<Files
																		size={20}
																		weight='fill'
																		className='text-h-primary'
																	/>
																	<Text
																		size='body-3'
																		weight='medium'
																	>
																		Parcialidad {installment.partiality_number}
																	</Text>
																</div>
																<DownloadSimple
																	size={18}
																	weight='bold'
																	className='text-h-primary'
																/>
															</button>
														),
													)}
												</div>
											</div>
										)}
								</>
							)}
						</div>
					</div>
				</Modal>
			</section>
		);
	}

	if (isLoading || isLoadingSat)
		return (
			<section className='py-4 px-2 md:py-6 md:px-12 flex flex-col gap-4 pb-10 text-sm max-w-[700px] mx-auto'>
				<Loading />
			</section>
		);

	return (
		<div className='p-2 md:p-12'>
			{showBackButton && (
				<div className='md:mb-[-40px]'>
					<button
						onClick={handleBackNavigation}
						className='flex items-center gap-1'
					>
						<CaretCircleLeft
							size={20}
							weight='fill'
							color='var(--h-primary)'
						/>
						<Text
							size='caption'
							color='medium'
							weight='medium'
						>
							Volver
						</Text>
					</button>
				</div>
			)}
			<section className='py-4 px-2 md:py-6 md:px-12 flex flex-col gap-4 pb-32 md:pb-10 text-sm md:text-base max-w-[700px] mx-auto fade-in-scale'>
				{!userIsLinked && (
					<AnnualCountdown
						page='preview'
						onClick={() => {
							navigate(ROUTES.PLANS_AND_PRICES);
						}}
					/>
				)}
				<ModalAnnualPreferences
					open={showModal.annualPreferences}
					onClose={() =>
						setShowModal((prev) => ({ ...prev, annualPreferences: false }))
					}
					fetchSupportIssues={fetchSupportIssues}
				/>
				<ModalPositiveBalance
					open={showModal.positiveBalance}
					onClose={() =>
						setShowModal((prev) => ({ ...prev, positiveBalance: false }))
					}
					fetchSupportIssues={fetchSupportIssues}
				/>

				<ModalPaymentInstallments
					open={showModal.paymentInstallments}
					fetchSupportIssues={fetchSupportIssues}
					onClose={() =>
						setShowModal((prev) => ({ ...prev, paymentInstallments: false }))
					}
				/>
				<Modal
					open={showModal.unlinked}
					onClose={() => setShowModal((prev) => ({ ...prev, unlinked: false }))}
					type='fullscreen'
					showCloseButton={true}
					sx='overflow-y-auto w-[90%] max-h-[calc(100dvh-32px)] md:max-h-[calc(100dvh-100px)] max-w-[700px] !p-1'
				>
					<UnlinkedUser
						showHeader={false}
						onSuccessLink={() => {
							window.location.reload();
						}}
					/>
				</Modal>

				{step < 3 && !userHasAnnualSubscription && userIsLinked && (
					<div className='relative rounded-lg overflow-hidden'>
						<button onClick={handleAddToCart}>
							<img
								src={isMobile ? BannerAnnualPreviewMobile : BannerAnnualPreview}
								alt='Banner Annual Preview'
								className='w-full border-2 border-white rounded-lg overflow-hidden shadow-sm'
							/>
						</button>
						{isLoadingAddToCart?.products && (
							<button
								title='Cargando...'
								disabled={isLoadingAddToCart?.products}
								className='absolute top-0 left-0 w-full h-full bg-white/40 flex items-center justify-center'
							>
								<Loading size={28} />
							</button>
						)}
					</div>
				)}

				<StepsAnnual
					numberOfSteps={MAX_STEPS}
					currentStep={step}
					setCurrentStep={setStep}
					isDisabled={!userIsLinked}
				/>

				{step === 1 && (
					<AnnualPreviewStep1
						data={annualDeclarationOverview}
						userIsLinked={userIsLinked}
						userHasPurchasedAnnualSubscription={userHasAnnualSubscription}
						setShowModal={setShowModal}
						supportIssues={supportIssues}
					/>
				)}
				{step === 2 && annualDeclarationOverview && (
					<AnnualPreviewStep2
						data={annualDeclarationOverview}
						userHasAnnualSubscription={userHasAnnualSubscription}
					/>
				)}
				{step === 3 && annualDeclarationOverview && (
					<AnnualPreviewStepFinal
						data={annualDeclarationOverview}
						handleAddToCart={handleAddToCart}
						isLoadingAddToCart={isLoadingAddToCart.products}
						havePrebillingAvailable={havePrebillingAvailable}
						userHasAnnualSubscription={userHasAnnualSubscription}
					/>
				)}

				{userIsLinked ? (
					<div className='flex gap-2 justify-between'>
						{step > 1 && (
							<PrimaryButton
								onClick={() => {
									setStep(step - 1);
								}}
								label='Volver'
								beforeIcon={
									<CaretLeft
										weight='bold'
										className='min-w-5 min-h-5'
									/>
								}
								variant='outline'
							/>
						)}
						{step !== MAX_STEPS && (
							<PrimaryButton
								onClick={() => {
									setStep(step + 1);
								}}
								variant={
									userHasPendingDeclarations && userHasAnnualSubscription
										? 'tertiary'
										: 'primary'
								}
								label='Continuar'
								icon={<CaretRight className='min-w-5 min-h-5' />}
								sx='h-12'
							/>
						)}
					</div>
				) : (
					<div className='flex gap-2 justify-between'>
						<PrimaryButton
							label='Vincular mi RFC'
							onClick={() =>
								setShowModal((prev) => ({ ...prev, unlinked: true }))
							}
						/>
					</div>
				)}
			</section>
		</div>
	);
};

export default AnnualDeclarationPage;
